import QRCode from 'qrcode';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { happenings, isEvening, STATE_LABEL, summarize, type TableSummary } from '../lib/floor';
import { starterPlan, type FloorPlan } from '../lib/floorplan';
import { laneOf, minutesSince } from '../lib/kitchen';
import { usePollWhileOffline } from '../lib/live';
import { staffSession } from '../lib/owner';
import { usePref } from '../lib/prefs';
import { navigate } from '../lib/router';
import { baht } from '../lib/text';
import type { Menu, Order, Takeout } from '../lib/types';
import { Checkout } from './Checkout';
import type { FloatEvent, Light } from './floor3d/Floor3D';
import type { Service } from './floor3d/scene';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

const Floor3D = lazy(() => import('./floor3d/Floor3D'));

interface Table { id: string; name: string; seats: number; session: { id: string; guests: number; openedAt: string; billRequestedAt?: string } | null }
type ViewPref = 'auto' | '3d' | 'simple';

let floatSeq = 0;

/** /floor: tables (simple tiles or the 3D floor) + open takeouts; seat, add dishes, check out. Live over WebSocket. */
export function Floor({ path }: { path: string }) {
  const [tables, setTables] = useState<Table[]>([]);
  const [takeout, setTakeout] = useState<Takeout[]>([]);
  /** Every order of every open sitting (served ones too: they are the plates on the table), by order id. */
  const [known, setKnown] = useState<Map<string, Order>>(new Map());
  const [art, setArt] = useState<Map<string, string | undefined>>(new Map());
  const [plan, setPlan] = useState<FloorPlan | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<{ id: string; title: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  const [err, setErr] = useState<string | null>(null);
  const [viewPref, setViewPref] = usePref<ViewPref>('floor.view', 'auto');
  const [room, setRoom] = usePref<string>('floor.room', 'all');
  const [noGl, setNoGl] = useState(false);
  const [floats, setFloats] = useState<FloatEvent[]>([]);
  const [light, setLight] = usePref<Light>('floor.light', 'auto');
  const isOwner = staffSession()?.role === 'owner';

  const loadPlan = useCallback(async (token: string) => {
    try { setPlan(await api<FloorPlan>('GET', '/floorplan', { token })); } catch (e) { handleAuthError(e); }
  }, []);
  const load = useCallback(async (token: string) => {
    try {
      const [t, k, to] = await Promise.all([
        api<{ tables: Table[] }>('GET', '/tables', { token }),
        api<{ orders: Order[] }>('GET', '/kitchen', { token }),
        api<{ takeout: Takeout[] }>('GET', '/takeout', { token }),
      ]);
      setTables(t.tables);
      setTakeout(to.takeout);
      const open = new Set([...t.tables.flatMap((x) => (x.session ? [x.session.id] : [])), ...to.takeout.map((x) => x.id)]);
      setKnown((prev) => {
        const next = new Map([...prev].filter(([, o]) => open.has(o.sessionId)));
        for (const o of k.orders) next.set(o.id, o);
        return next;
      });
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, []);

  const { s, online } = useStaff((e) => {
    if (e.kind === 'table' || e.kind === 'session') { if (s) void load(s.token); }
    if (e.kind === 'floorplan') { if (s) void loadPlan(s.token); }
    if (e.kind === 'order') {
      const o = e.order as Order;
      setKnown((prev) => new Map(prev).set(o.id, o));
    }
    if (e.kind === 'menu' && s) void loadArt(s.token);
  }, () => { if (s) { void load(s.token); void loadPlan(s.token); } });
  useEffect(() => { if (s) { void load(s.token); void loadPlan(s.token); } }, [s, load, loadPlan]);
  usePollWhileOffline(online, () => { if (s) void load(s.token); });
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  // A sitting's earlier orders (already served, so no longer on the kitchen board): fetched once per sitting.
  const history = useRef(new Set<string>());
  useEffect(() => {
    if (!s) return;
    const missing = tables.flatMap((t) => (t.session && !history.current.has(t.session.id) ? [t.session.id] : []));
    if (!missing.length) return;
    for (const id of missing) history.current.add(id);
    void (async () => {
      for (let i = 0; i < missing.length; i += 6) {
        const got = await Promise.all(missing.slice(i, i + 6).map((id) =>
          api<{ orders: Order[] }>('GET', `/sessions/${id}/orders`, { token: s.token }).then((r) => r.orders, () => [] as Order[])));
        setKnown((prev) => {
          const next = new Map(prev);
          for (const o of got.flat()) if (!next.has(o.id)) next.set(o.id, o);
          return next;
        });
      }
    })();
  }, [tables, s]);

  // Dish stickers for the plates in 3D, from the published menu.
  const loadArt = useCallback(async (token: string) => {
    try {
      const m = await api<Menu>('GET', '/menu', { token });
      setArt(new Map(m.categories.flatMap((c) => c.items).map((i) => [i.id, i.media?.sticker])));
    } catch { /* no menu yet: plates show plain food */ }
  }, []);

  const bySession = useMemo(() => {
    const m = new Map<string, Order[]>();
    for (const o of known.values()) m.set(o.sessionId, [...(m.get(o.sessionId) ?? []), o]);
    return m;
  }, [known]);

  const summaries = useMemo(() => new Map<string, TableSummary>(tables.map((t) =>
    [t.id, summarize(t.session, t.session ? bySession.get(t.session.id) ?? [] : [], now)])), [tables, bySession, now]);

  // Something happened at a table -> it floats up in 3D (new order, dishes ready, bill please...).
  const prev = useRef<Map<string, TableSummary> | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = summaries;
    if (!before || !tables.length) return;
    const fresh: FloatEvent[] = [];
    for (const t of tables) {
      const next = summaries.get(t.id)!;
      for (const h of happenings(before.get(t.id), next, t.session ? bySession.get(t.session.id) ?? [] : [], Date.now())) fresh.push({ id: ++floatSeq, tableId: t.id, ...h });
    }
    if (fresh.length) setFloats((f) => [...f, ...fresh].slice(-30));
  }, [summaries, tables, bySession]);

  const tileState = (sum: TableSummary | undefined) => sum ?? summarize(null, [], now);
  const takeoutState = (t: Takeout) => {
    if (!t.accepted) return { cls: 'pending', label: 'รอยืนยัน · New online order' };
    const sum = summarize({ id: t.id, openedAt: t.openedAt, billRequestedAt: t.billRequestedAt }, bySession.get(t.id) ?? [], now);
    return { cls: sum.state, label: STATE_LABEL[sum.state] };
  };

  const newTakeout = async () => {
    if (!s) return;
    const name = prompt('ชื่อลูกค้า (ไม่บังคับ) · Customer name (optional)') ?? '';
    try {
      const t = await api<Takeout>('POST', '/takeout', { token: s.token, body: name.trim() ? { name: name.trim() } : {} });
      navigate(`/order/${t.id}`);
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  };
  const accept = async (t: Takeout) => {
    if (!s) return;
    try { await api('POST', `/takeout/${t.id}/accept`, { token: s.token }); void load(s.token); } catch (e) { setErr((e as Error).message); }
  };

  const shownPlan = useMemo(() => (plan && plan.rooms.length ? plan : starterPlan(tables, staffSession()?.name)), [plan, tables]);
  const tableRefs = useMemo(() => tables.map((t) => ({ id: t.id, name: t.name, seats: t.seats })), [tables]);
  const mode: '3d' | 'simple' = noGl ? 'simple' : viewPref === 'auto' ? (plan && plan.rev > 0 ? '3d' : 'simple') : viewPref;
  const onFail = useCallback(() => setNoGl(true), []);
  useEffect(() => { if (mode === '3d' && s && !art.size) void loadArt(s.token); }, [mode, s, art.size, loadArt]);

  // The kitchen and counter in 3D: tickets for orders still cooking, ready dishes at the pass, bags for takeout.
  const service = useMemo<Service>(() => {
    const active = [...known.values()].filter((o) => { const lane = laneOf(o); return lane !== null; });
    const lines = active.flatMap((o) => o.lines);
    return {
      tickets: active.filter((o) => { const lane = laneOf(o); return lane === 'new' || lane === 'cooking'; }).length,
      cooking: lines.filter((l) => l.status === 'queued' || l.status === 'cooking').reduce((n, l) => n + l.qty, 0),
      ready: lines.filter((l) => l.status === 'ready').flatMap((l) => Array<string | null>(Math.min(l.qty, 4)).fill(art.get(l.itemId) ?? null)).slice(0, 12),
      takeout: takeout.filter((t) => t.accepted).length,
    };
  }, [known, art, takeout]);
  const evening = light === 'evening' || (light === 'auto' && isEvening(now));
  const sessionOf = (tableId: string) => tables.find((t) => t.id === tableId)?.session ?? null;

  const seated = tables.filter((t) => t.session).length;
  const selected = tables.find((t) => t.id === sel) ?? null;
  return (
    <Shell path={path} online={online} sub={`${seated}/${tables.length} tables · ${takeout.length} takeout`}>
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}

      <section className="panel" aria-label="Takeout" style={{ marginBottom: 14 }}>
        <div className="row" style={{ marginBottom: takeout.length ? 10 : 0 }}>
          <h2 className="display" style={{ margin: 0, fontSize: 18 }}>กลับบ้าน · Takeout</h2>
          <button className="btn-mango" onClick={() => void newTakeout()}>+ สั่งกลับบ้าน · New takeout</button>
        </div>
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto' }}>
          {takeout.map((t) => {
            const st = takeoutState(t);
            return (
              <div key={t.id} className={`tile ${st.cls}`} style={{ minWidth: 200, cursor: 'default' }}>
                <div className="row" style={{ width: '100%' }}>
                  <span className="display" style={{ fontSize: 20 }}>{t.pickupNo}</span>
                  <span className="tchip">{st.label}</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{[t.customerName, t.phone].filter(Boolean).join(' · ') || (t.source === 'online' ? 'Online' : 'Counter')} · {minutesSince(t.openedAt, now)} min</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {!t.accepted && <button className="dish-step queued" onClick={() => void accept(t)}>รับออร์เดอร์ · Accept</button>}
                  <button className="dish-step" onClick={() => navigate(`/order/${t.id}`)}>+ เมนู</button>
                  <button className="dish-step ready" onClick={() => setCheckout({ id: t.id, title: t.pickupNo })}>เช็คบิล</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="row" style={{ marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
        <h2 className="display" style={{ margin: 0, fontSize: 18 }}>ผังร้าน · Floor</h2>
        <div className="seg3d" role="group" aria-label="Floor view">
          <button aria-pressed={mode === 'simple'} onClick={() => setViewPref('simple')}>แบบง่าย · Simple</button>
          <button aria-pressed={mode === '3d'} disabled={noGl} onClick={() => setViewPref('3d')}>3D</button>
        </div>
      </div>
      {noGl && <div className="warn" style={{ color: 'var(--ink)', marginBottom: 12 }}>เครื่องนี้แสดง 3D ไม่ได้ ใช้แบบง่ายแทน · This device can't show 3D, so the simple view is on.</div>}

      <div className="layout">
        {mode === '3d' ? (
          <section className="main" aria-label="Floor plan (3D)">
            <Suspense fallback={<div className="stage3d"><div className="spinner" role="status" aria-label="Loading 3D floor" style={{ margin: 'auto' }} /></div>}>
              <Floor3D plan={shownPlan} tables={tableRefs} summaries={summaries} floats={floats} art={art} service={service}
                evening={evening} light={light} onLight={setLight}
                onFloatDone={(id) => setFloats((f) => f.filter((x) => x.id !== id))}
                selected={sel} onSelect={setSel} view={room} onView={setRoom}
                onOrder={(id) => { const ss = sessionOf(id); if (ss) navigate(`/order/${ss.id}`); }}
                onCheckout={(id) => { const ss = sessionOf(id); const t = tables.find((x) => x.id === id); if (ss && t) setCheckout({ id: ss.id, title: `โต๊ะ ${t.name}` }); }}
                onDetails={() => document.querySelector('.aside')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                canEdit={isOwner} onEdit={() => navigate('/floor/edit')} onFail={onFail} />
            </Suspense>
          </section>
        ) : (
          <section className="floorplan main" aria-label="Floor plan">
            {isOwner && plan && plan.rev === 0 && !noGl && (
              <div className="row" style={{ marginBottom: 14, background: '#FFF1CC', border: 'var(--line)', borderRadius: 16, padding: '10px 14px' }}>
                <span style={{ fontWeight: 700 }}>จัดผังร้านให้เหมือนร้านจริงแบบ 3D · Lay out your real floor in 3D</span>
                <button className="btn-mango" onClick={() => navigate('/floor/edit')}>เริ่มจัดร้าน · Start</button>
              </div>
            )}
            <div className="tiles">
              {tables.map((t) => {
                const sum = tileState(summaries.get(t.id));
                return (
                  <button key={t.id} className={`tile ${sum.state}`} aria-pressed={sel === t.id} onClick={() => setSel(t.id)} aria-label={`Table ${t.name}, ${STATE_LABEL[sum.state]}`}>
                    <div className="row" style={{ width: '100%' }}>
                      <span className="display" style={{ fontSize: 20 }}>{t.name}</span>
                      <span className="tchip" style={sum.state === 'late' ? { background: 'var(--red)' } : sum.state === 'bill' ? { background: '#6A44D8' } : undefined}>{sum.ready > 0 && sum.state !== 'late' && sum.state !== 'bill' ? `พร้อมเสิร์ฟ ${sum.ready} · Ready` : STATE_LABEL[sum.state]}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {t.session ? `${t.session.guests || '–'} guests · ${minutesSince(t.session.openedAt, now)} min` : `${t.seats} seats`}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        <aside className="aside">
          {selected ? (
            <TablePanel key={selected.id + (selected.session?.id ?? '')} t={selected} token={s?.token ?? ''}
              onChange={() => s && void load(s.token)} onCheckout={(id) => setCheckout({ id, title: `โต๊ะ ${selected.name}` })} />
          ) : <div className="panel"><p style={{ margin: 0, color: '#A9AFC6' }}>แตะโต๊ะเพื่อเปิด สั่งอาหาร หรือเช็คบิล · Tap a table to seat, order or check out.</p></div>}
        </aside>
      </div>

      {checkout && s && (
        <Checkout token={s.token} sessionId={checkout.id} title={checkout.title} onClose={() => setCheckout(null)}
          onDone={() => { setCheckout(null); setSel(null); void load(s.token); }} />
      )}
    </Shell>
  );
}

function TablePanel({ t, token, onChange, onCheckout }: { t: Table; token: string; onChange: () => void; onCheckout: (sessionId: string) => void }) {
  const [guests, setGuests] = useState(2);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [qr, setQr] = useState('');
  const [bill, setBill] = useState<{ totalSatang: number; lines: unknown[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!t.session) return;
    void api<{ joinUrl: string }>('GET', `/sessions/${t.session.id}/join`, { token }).then((r) => setJoinUrl(r.joinUrl), () => undefined);
    void api<{ totalSatang: number; lines: unknown[] }>('GET', `/sessions/${t.session.id}/bill`, { token }).then(setBill, () => undefined);
  }, [t.session, token]);
  useEffect(() => {
    if (joinUrl) void QRCode.toString(joinUrl, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }).then(setQr);
  }, [joinUrl]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try { await fn(); onChange(); } catch (e) { handleAuthError(e); setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className="panel side" aria-label={`Table ${t.name}`}>
      <h2 className="display" style={{ margin: 0, fontSize: 22 }}>โต๊ะ {t.name}</h2>
      {!t.session ? (
        <>
          <label className="field" style={{ color: '#F2F0E9' }}>จำนวนลูกค้า · Guests
            <div className="stepper" style={{ alignSelf: 'flex-start', color: 'var(--ink)' }}>
              <button onClick={() => setGuests(Math.max(1, guests - 1))} aria-label="Fewer guests">−</button>
              <span>{guests}</span>
              <button onClick={() => setGuests(Math.min(100, guests + 1))} aria-label="More guests">+</button>
            </div>
          </label>
          <button className="btn-mango" disabled={busy} onClick={() => void act(async () => {
            const r = await api<{ joinUrl: string }>('POST', `/tables/${t.id}/sessions`, { token, body: { guests } });
            setJoinUrl(r.joinUrl);
          })}>เปิดโต๊ะ · Seat guests</button>
        </>
      ) : (
        <>
          {t.session.billRequestedAt && <div className="warn" style={{ color: 'var(--ink)' }}>ลูกค้าขอเช็คบิล · Guests asked for the bill</div>}
          {qr ? (
            <>
              <div className="qr" role="img" aria-label={`QR code to order at table ${t.name}`} dangerouslySetInnerHTML={{ __html: qr }} />
              <span style={{ fontSize: 13, color: '#A9AFC6', textAlign: 'center' }}>ให้ลูกค้าสแกนเพื่อสั่งอาหาร · Guests scan to order</span>
            </>
          ) : <div className="spinner" role="status" aria-label="Loading QR" style={{ alignSelf: 'center' }} />}
          {bill && <div className="row" style={{ fontWeight: 700 }}><span>{bill.lines.length} dishes</span><span className="display" style={{ color: 'var(--mango)' }}>{baht(bill.totalSatang)}</span></div>}
          <button className="btn-dark" onClick={() => navigate(`/order/${t.session!.id}`)}>+ สั่งอาหาร · Add dishes</button>
          <button className="btn-mango" onClick={() => onCheckout(t.session!.id)}>เช็คบิล · Checkout</button>
          {bill && bill.lines.length === 0 && (
            <button className="btn-dark" disabled={busy} onClick={() => void act(() => api('POST', `/sessions/${t.session!.id}/close`, { token }))}>ปิดโต๊ะ (ไม่มีรายการ) · Close (nothing ordered)</button>
          )}
        </>
      )}
      {err && <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>{err}</div>}
    </section>
  );
}
